import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import {
  DollarSign,
  Music,
  Globe,
  TrendingUp,
  PlayCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ArtistPortalEarnings() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEarnings = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/royalties/my-earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This page is only available for artists.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load earnings data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-GB').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="error-message">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Unable to Load Earnings</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  // Show message if no data yet
  if (!earnings || earnings.total_revenue === 0) {
    return (
      <div className="space-y-6" data-testid="artist-earnings-page">
        <div>
          <h1 className="text-2xl font-bold font-manrope">My Earnings</h1>
          <p className="text-muted-foreground text-sm">View your streaming royalties and earnings breakdown</p>
        </div>
        
        <Card className="p-8 text-center">
          <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No Earnings Data Yet</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            {earnings?.message || 'Your royalty statements will appear here once your manager uploads them. Check back soon!'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="artist-earnings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-manrope">My Earnings</h1>
        <p className="text-muted-foreground text-sm">
          Streaming royalties for <span className="text-primary font-medium">{earnings.artist_name}</span>
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-green-500/10 border-green-500/30 p-4" data-testid="total-earnings-card">
          <div className="flex items-center gap-2 text-green-500 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Total Earnings</span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(earnings.total_revenue)}</p>
        </Card>
        
        <Card className="bg-blue-500/10 border-blue-500/30 p-4" data-testid="total-streams-card">
          <div className="flex items-center gap-2 text-blue-500 mb-1">
            <PlayCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Total Streams</span>
          </div>
          <p className="text-2xl font-bold">{formatNumber(earnings.total_streams)}</p>
        </Card>
        
        <Card className="bg-purple-500/10 border-purple-500/30 p-4" data-testid="tracks-card">
          <div className="flex items-center gap-2 text-purple-500 mb-1">
            <Music className="w-4 h-4" />
            <span className="text-sm font-medium">Tracks</span>
          </div>
          <p className="text-2xl font-bold">{earnings.track_count}</p>
        </Card>
        
        <Card className="bg-orange-500/10 border-orange-500/30 p-4" data-testid="platforms-card">
          <div className="flex items-center gap-2 text-orange-500 mb-1">
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium">Platforms</span>
          </div>
          <p className="text-2xl font-bold">{Object.keys(earnings.by_dsp || {}).length}</p>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Earnings by Platform */}
        <Card className="p-4" data-testid="earnings-by-platform">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-500" />
            Earnings by Platform
          </h3>
          <div className="space-y-2">
            {Object.entries(earnings.by_dsp || {}).map(([dsp, data]) => (
              <div key={dsp} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{dsp}</p>
                  <p className="text-xs text-muted-foreground">{formatNumber(data.streams)} streams</p>
                </div>
                <p className="font-semibold text-green-500">{formatCurrency(data.revenue)}</p>
              </div>
            ))}
            {Object.keys(earnings.by_dsp || {}).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No platform data available</p>
            )}
          </div>
        </Card>

        {/* Earnings by Territory */}
        <Card className="p-4" data-testid="earnings-by-territory">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            Earnings by Territory
          </h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {Object.entries(earnings.by_territory || {}).map(([territory, data]) => (
              <div key={territory} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{territory}</p>
                  <p className="text-xs text-muted-foreground">{formatNumber(data.streams)} streams</p>
                </div>
                <p className="font-semibold text-green-500">{formatCurrency(data.revenue)}</p>
              </div>
            ))}
            {Object.keys(earnings.by_territory || {}).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No territory data available</p>
            )}
          </div>
        </Card>
      </div>

      {/* Top Tracks */}
      <Card className="p-4" data-testid="top-tracks">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Music className="w-4 h-4 text-orange-500" />
          Top Tracks
        </h3>
        <div className="space-y-2">
          {(earnings.tracks || []).slice(0, 10).map((track, idx) => (
            <div key={track.isrc || `track-${idx}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-muted-foreground w-6">{idx + 1}</span>
                <div>
                  <p className="font-medium text-sm">{track.track_title || 'Unknown Track'}</p>
                  <p className="text-xs text-muted-foreground">{track.isrc}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-green-500">{formatCurrency(track.revenue)}</p>
                <p className="text-xs text-muted-foreground">{formatNumber(track.streams)} streams</p>
              </div>
            </div>
          ))}
          {(earnings.tracks || []).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No track data available</p>
          )}
        </div>
      </Card>
    </div>
  );
}
